import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight, ShoppingCart } from 'lucide-react';
import Header from './components/Header5';
import Footer from './components/Footer5';
import Button from './components/Button';

/**
 * Template 5: TARA — Cart Page
 * Clean editorial cart with charcoal/cream aesthetic
 */
const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Chanderi Silk Saree',
            price: 12999,
            quantity: 1,
            size: 'M',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 2,
            name: 'Embroidered Lehenga',
            price: 34999,
            quantity: 1,
            size: 'S',
            image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 3,
            name: 'Banarasi Silk Kurta',
            price: 8499,
            quantity: 2,
            size: 'L',
            image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems((items) =>
            items.map((item) =>
                item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems((items) => items.filter((item) => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = subtotal >= 2999 ? 0 : 199;
    const discount = appliedCoupon ? Math.round(subtotal * 0.1) : 0;
    const total = subtotal + shipping - discount;

    return (
        <div className="w-full min-h-screen" style={{ backgroundColor: '#FAF9F6', fontFamily: "'Outfit', sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap');
            `}</style>
            <Header cartCount={cartItems.length} />

            <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
                <h1
                    className="text-3xl md:text-4xl text-[#1A1A1A] mb-2 text-center"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                    Your Cart
                </h1>
                <p className="text-sm text-[#71717A] text-center mb-8 md:mb-12 font-light">
                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
                </p>

                {cartItems.length === 0 ? (
                    <div className="text-center py-20">
                        <ShoppingCart className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
                        <p
                            className="text-xl text-[#71717A] italic mb-6"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                            Your cart is empty
                        </p>
                        <Button variant="primary" onClick={() => navigate('/template5/shop')}>
                            Continue Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Cart Items */}
                        <div className="lg:col-span-2">
                            <div className="space-y-0">
                                {cartItems.map((item) => (
                                    <div key={item.id} className="flex gap-4 md:gap-6 py-6 border-b border-[#E4E4E7]">
                                        <div
                                            className="w-20 h-24 md:w-28 md:h-36 overflow-hidden bg-[#F5F4F0] flex-shrink-0 cursor-pointer"
                                            onClick={() => navigate(`/template5/product/${item.id}`)}
                                        >
                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 flex flex-col justify-between">
                                            <div>
                                                <h3
                                                    className="text-base md:text-lg text-[#1A1A1A] mb-1 cursor-pointer hover:text-[#71717A] transition-colors"
                                                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                                    onClick={() => navigate(`/template5/product/${item.id}`)}
                                                >
                                                    {item.name}
                                                </h3>
                                                <p className="text-xs text-[#71717A] mb-2 font-light">Size: {item.size}</p>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="inline-flex items-center border border-[#E4E4E7]">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, -1)}
                                                        className="w-8 h-8 flex items-center justify-center text-[#71717A] hover:text-[#1A1A1A] transition-colors"
                                                    >
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="w-8 h-8 flex items-center justify-center text-xs text-[#1A1A1A] border-x border-[#E4E4E7]">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, 1)}
                                                        className="w-8 h-8 flex items-center justify-center text-[#71717A] hover:text-[#1A1A1A] transition-colors"
                                                    >
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm text-[#1A1A1A]" style={{ fontWeight: 500 }}>
                                                        ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                                                    </span>
                                                    <button
                                                        onClick={() => removeItem(item.id)}
                                                        className="text-[#71717A] hover:text-[#1A1A1A] transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="bg-[#F5F4F0] p-6 md:p-8">
                                <h2
                                    className="text-xl md:text-2xl text-[#1A1A1A] mb-6"
                                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                                >
                                    Order Summary
                                </h2>

                                {/* Coupon */}
                                <div className="mb-6 pb-6 border-b border-[#E4E4E7]">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            placeholder="Coupon code"
                                            className="flex-1 px-3 py-2.5 text-xs bg-white border border-[#E4E4E7] text-[#1A1A1A] placeholder-[#71717A] outline-none focus:border-[#2C2C2C] transition-colors"
                                        />
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => { if (couponCode) setAppliedCoupon(couponCode); }}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                    {appliedCoupon && (
                                        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                            <Tag size={12} /> 10% discount applied
                                        </p>
                                    )}
                                </div>

                                {/* Totals */}
                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#71717A] font-light">Subtotal</span>
                                        <span className="text-[#1A1A1A]" style={{ fontWeight: 500 }}>₹{subtotal.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-[#71717A] font-light">Shipping</span>
                                        <span className="text-[#1A1A1A]" style={{ fontWeight: 500 }}>
                                            {shipping === 0 ? 'Complimentary' : `₹${shipping}`}
                                        </span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-[#71717A] font-light">Discount</span>
                                            <span className="text-green-600" style={{ fontWeight: 500 }}>-₹{discount.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between pt-4 border-t border-[#E4E4E7]">
                                        <span className="text-base text-[#1A1A1A]" style={{ fontWeight: 500 }}>Total</span>
                                        <span className="text-lg text-[#1A1A1A]" style={{ fontWeight: 600 }}>₹{total.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>

                                <Button variant="primary" fullWidth onClick={() => navigate('/template5/checkout')}>
                                    Proceed to Checkout <ArrowRight className="w-4 h-4" />
                                </Button>

                                <button
                                    onClick={() => navigate('/template5/shop')}
                                    className="w-full mt-3 text-center text-xs text-[#71717A] tracking-wide underline underline-offset-4 hover:text-[#1A1A1A] transition-colors py-2"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default Cart;
